import React, { useState } from "react";
import useSortableData from "../../utils/SortData";
import DetailBox from "./DetailBox";
import { Eye } from "lucide-react";
import { TablerCaretUpDownFilled } from "../../assets/icons/icons";
import EmptyTableRecord from "../layout/EmptyTableRecord";
import { formatDateOnly, formatDateParts } from "../../utils/DateFormat";

const TABLE_HEADERS = [
  { key: "uid", label: "ID" },
  { key: "oldMobileNumber", label: "Customer old number" },
  { key: "customerFirstName", label: "First name" },
  { key: "customerLastName", label: "Last name" },
  { key: "dateOfBirth", label: "Date of Birth" },
  { key: "pukCode", label: "PUK Code " },
  { key: "newSimSerialNumber", label: "New Sim Serial Number" },
  { key: "createdAt", label: "Date & Time" },
  { key: "status", label: "Status", isStatus: true },
  { key: "action", label: "Action", isAction: true },
];

export function getStatusClass(status) {
  const baseClass = "px-5 py-1 rounded-xl text-xs font-medium text-center w-24";
  if (["OPEN"].includes(status)) {
    return `bg-[#DBEAFE] text-[#1E40AF] ${baseClass}`;
  } else if (status === "IN_REVIEW") {
    return `bg-yellow-100 text-[#92400E] ${baseClass}`;
  } else if (["APPROVED"].includes(status)) {
    return `bg-[#D1FAE5] text-[#065F46] ${baseClass}`;
  } else if (status === "REJECTED") {
    return `bg-[#FEE2E2] text-[#991B1B] ${baseClass}`;
  } else {
    return `text-gray-800 ${baseClass}`;
  }
}

const SimSwapTable = ({ simSwapData, SIMSWAP_STATUSES }) => {
  const [selectedSimSwapData, setSelectedSimSwapData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { sortedItems: sortedSimSwapData, handleSort } =
    useSortableData(simSwapData);

  const openModal = (simSwap) => {
    setSelectedSimSwapData(simSwap);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedSimSwapData(null);
  };

  // Function to format status text (first letter capital, rest lowercase)
  const formatStatus = (status) => {
    if (!status) return "";
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  return (
    <div className=" rounded-lg border-t border-x border-b border-gray-300 overflow-x-auto">
      <table className="w-full  border-gray-300 rounded-md">
        <thead className="bg-gray-100 border-b">
          <tr className="bg-gray-100 md:h-10">
            {TABLE_HEADERS.map((header, index) => (
              <th
                key={index}
                className="py-3 text-center font-semibold text-text1 whitespace-nowrap px-2 text-[15px] md:h-10"
              >
                {header.key !== "action" ? (
                  <div
                    className="flex items-center cursor-pointer justify-start"
                    onClick={() => handleSort(header.key)}
                  >
                    <TablerCaretUpDownFilled className="mr-2" />
                    {header.label}
                  </div>
                ) : (
                  <div className="flex justify-start items-center">
                    {header.label}
                  </div>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {sortedSimSwapData.length > 0 ? (
            sortedSimSwapData.map((simSwap) => (
              <tr key={simSwap._id} className="hover:bg-gray-50 md:h-[60px]">
                {TABLE_HEADERS.map((header) => (
                  <td
                    key={header.key}
                    className=" pl-2 text-[15px] md:h-[60px] text-[#374151] text-left"
                  >
                    {header.key === "createdAt" ? (
                      <span className="whitespace-nowrap">
                         {(() => {
                            const { date, time } = formatDateParts(simSwap[header.key]);
                            return (
                                <>
                                  <div>{date}</div>
                                  <div>{time}</div>
                                </>
                           );
                          })()}
                      </span>
                    ) : header.key === "dateOfBirth" ? (
                      simSwap[header.key] ? (
                        formatDateOnly(simSwap[header.key])
                      ) : (
                        "N/A"
                      )
                    ) : header.isAction ? (
                      <div className="flex justify-start items-center">
                        <button
                          onClick={() => openModal(simSwap)}
                          className="px-2 rounded"
                        >
                          <Eye className="h-5 w-5 text-gray-400" />
                        </button>
                      </div>
                    ) : header.isStatus ? (
                      <span className={getStatusClass(simSwap.status)}>
                        {formatStatus(simSwap.status)}
                      </span>
                    ) : (
                      header.key
                        .split(".")
                        .reduce(
                          (simSwap, key) => (simSwap ? simSwap[key] : "N/A"),
                          simSwap
                        ) || "N/A"
                    )}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={TABLE_HEADERS.length}
                className=" text-center text-gray-500"
              >
                <EmptyTableRecord />
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {isModalOpen && selectedSimSwapData && (
        <DetailBox
          closeModal={closeModal}
          selectedSimSwap={selectedSimSwapData}
          getStatusClass={getStatusClass}
          formatStatus={formatStatus}
          SIMSWAP_STATUSES={SIMSWAP_STATUSES}
        />
      )}
    </div>
  );
};

export default SimSwapTable;
